import React, { useState, useEffect, useRef, useMemo } from 'react';
import { create } from 'zustand';
import { 
    FileCog, LogOut, Settings, User, Users, PlusCircle, Trash2, FileDown, Bot, AlertTriangle, Upload, CalendarPlus
} from 'lucide-react';

// --- URL de votre API Backend ---
const API_URL = '/api';

// --- GESTION D'ÉTAT (ZUSTAND) ---
const useAppStore = create((set, get) => ({
    settings: {},
    employees: [],
    clients: [],
    expenses: [],
    isAuthenticated: false,
    currentPage: 'dashboard',
    selectedEmployeeId: null,
    isDataLoaded: false,
    apiError: null,

    fetchInitialData: async () => {
        try {
            const response = await fetch(`${API_URL}/all`);
            if (!response.ok) throw new Error(`Erreur réseau : ${response.statusText}`);
            const data = await response.json();
            set({
                settings: data.settings,
                employees: data.employees,
                clients: data.clients,
                expenses: data.expenses.map(e => ({...e, date: new Date(e.date).toISOString().split('T')[0]})), // Normaliser les dates
                isDataLoaded: true,
                apiError: null,
            });
        } catch (error) {
            console.error("Impossible de charger les données:", error);
            set({ apiError: "Connexion au serveur backend impossible. Veuillez vérifier que le serveur est bien démarré et que l'URL de l'API est correcte.", isDataLoaded: true });
        }
    },

    login: (pin) => {
        if (get().settings.loginPin && pin === get().settings.loginPin) {
            set({ isAuthenticated: true });
            return true;
        }
        return false;
    },
    logout: () => set({ isAuthenticated: false, currentPage: 'dashboard', selectedEmployeeId: null }),
    setPage: (page, employeeId = null) => set({ currentPage: page, selectedEmployeeId: employeeId }),

    addExpense: (newExpense) => set(state => ({ expenses: [...state.expenses, {...newExpense, date: new Date(newExpense.date).toISOString().split('T')[0]}] })),
    deleteExpense: (expenseId) => set(state => ({ expenses: state.expenses.filter(e => e.id !== expenseId) })),
    setExpenses: (expenses) => set({ expenses: expenses.map(e => ({...e, date: new Date(e.date).toISOString().split('T')[0]})) }),
    addClient: (newClient) => set(state => ({ clients: [...state.clients, newClient].sort((a,b) => a.name.localeCompare(b.name)) })),
    deleteClient: (clientId) => set(state => ({ clients: state.clients.filter(c => c.id !== clientId) })),
    updateSettings: (newSettings) => set({ settings: newSettings }),
}));


// --- FONCTIONS UTILITAIRES ---
const cn = (...classes) => classes.filter(Boolean).join(' ');

// --- COMPOSANTS UI ---
const Card=({className,...props})=>(<div className={cn("rounded-xl border border-slate-700 bg-slate-900/80 backdrop-blur-sm text-slate-50 shadow-lg shadow-cyan-500/10",className)} {...props}/>);
const CardHeader=({className,...props})=>(<div className={cn("flex flex-col space-y-1.5 p-6",className)} {...props}/>);
const CardTitle=({className,...props})=>(<h3 className={cn("text-2xl font-semibold leading-none tracking-tight text-cyan-300",className)} {...props}/>);
const CardDescription=({className,...props})=>(<p className={cn("text-sm text-slate-400",className)} {...props}/>);
const CardContent=({className,...props})=>(<div className={cn("p-6 pt-0",className)} {...props}/>);
const CardFooter=({className,...props})=>(<div className={cn("flex items-center p-6 pt-0",className)} {...props}/>);
const Button=React.forwardRef(({className,variant,size,...props},ref)=>{const variants={default:"bg-cyan-500 text-slate-900 hover:bg-cyan-400/90",destructive:"bg-red-600 text-slate-50 hover:bg-red-500/90",outline:"border border-cyan-500 bg-transparent hover:bg-cyan-500/20",ghost:"hover:bg-slate-700 hover:text-slate-50"};const sizes={default:"h-10 px-4 py-2",sm:"h-9 rounded-md px-3",lg:"h-11 rounded-md px-8",icon:"h-10 w-10"};return(<button className={cn("inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",variants[variant]||variants.default,sizes[size]||sizes.default,className)} ref={ref} {...props}/>)});
const Input=React.forwardRef(({className,...props},ref)=>(<input className={cn("flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-50 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",className)} ref={ref} {...props}/>));
const Select=React.forwardRef(({className,children,...props},ref)=>(<select className={cn("flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-50 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",className)} ref={ref} {...props}>{children}</select>));
const Label=({className,...props})=>(<label className={cn("text-sm font-medium leading-none text-slate-300 peer-disabled:cursor-not-allowed peer-disabled:opacity-70",className)} {...props}/>);
const Modal=({isOpen,onClose,title,children})=>{if(!isOpen)return null;return(<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"><div className="relative m-4 w-full max-w-lg"><Card><CardHeader><CardTitle>{title}</CardTitle></CardHeader><CardContent>{children}</CardContent></Card></div></div>)};

// --- ÉCRAN DE CONNEXION ---
const LoginScreen = () => {
    const [pin, setPin] = useState(['', '', '', '']);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login, isDataLoaded, apiError } = useAppStore();
    const inputsRef = useRef([]);

    const handlePinChange = (e, index) => {
        const { value } = e.target;
        if (/^[0-9]$/.test(value) || value === '') {
            const newPin = [...pin]; newPin[index] = value; setPin(newPin);
            if (value && index < 3) inputsRef.current[index + 1].focus();
        }
    };
    const handleKeyDown = (e, index) => { if (e.key === 'Backspace' && !pin[index] && index > 0) inputsRef.current[index - 1].focus(); };

    useEffect(() => {
        const checkPin = async () => {
            if (pin.every(p => p !== '')) {
                setIsLoading(true); setError('');
                await new Promise(res => setTimeout(res, 300));
                if (login(pin.join(''))) {
                    // C'est bon, on rentre
                } else {
                    setError('Code PIN incorrect.'); setPin(['', '', '', '']); inputsRef.current[0].focus();
                }
                setIsLoading(false);
            }
        };
        if (isDataLoaded && !apiError) checkPin();
    }, [pin, login, isDataLoaded, apiError]);

    if (!isDataLoaded) {
        return <div className="flex min-h-screen items-center justify-center bg-slate-900 text-cyan-400">Chargement des données depuis le serveur...</div>
    }

    if(apiError) {
        return <ApiErrorScreen message={apiError} />
    }

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-slate-900 p-4 font-sans text-slate-50">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
            <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-br from-slate-900 via-transparent to-slate-900"></div>
            <Card className="z-10 w-full max-w-sm">
                <CardHeader className="text-center">
                    <CardTitle>EASY HIGH T</CardTitle>
                    <CardDescription>Veuillez entrer votre code PIN</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex justify-center space-x-2 sm:space-x-4">
                        {pin.map((digit, index) => (
                            <Input key={index} ref={el => inputsRef.current[index] = el} type="password" inputMode="numeric" maxLength="1" value={digit} onChange={e => handlePinChange(e, index)} onKeyDown={e => handleKeyDown(e, index)} className="h-14 w-12 text-center text-2xl font-bold" disabled={isLoading} autoFocus={index === 0}/>
                        ))}
                    </div>
                    {error && <p className="mt-4 text-center text-red-400">{error}</p>}
                    {isLoading && <p className="mt-4 text-center text-cyan-400">Vérification...</p>}
                </CardContent>
            </Card>
        </div>
    );
};

// --- BARRE LATÉRALE ---
const Sidebar = () => {
    const { logout, setPage, currentPage, selectedEmployeeId, employees } = useAppStore();
    
    const NavItem = ({ icon, label, onClick, isActive }) => (
        <button onClick={onClick} className={cn("flex w-full items-center space-x-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors", isActive ? "bg-cyan-500/20 text-cyan-300" : "text-slate-300 hover:bg-slate-700")}>
            {icon}
            <span>{label}</span>
        </button>
    );

    return (
        <aside className="flex h-screen w-64 flex-col space-y-4 border-r border-slate-800 bg-slate-900 p-4">
            <div className="text-center">
                <h1 className="text-2xl font-bold text-cyan-400">Note de Frais</h1>
                <p className="text-xs text-slate-500">EASY HIGH T</p>
            </div>
            <nav className="flex-grow space-y-2">
                <h2 className="px-3 text-xs font-semibold uppercase text-slate-500">Menu</h2>
                <NavItem icon={<Users size={18}/>} label="Tableau de Bord" onClick={() => setPage('dashboard')} isActive={currentPage === 'dashboard'}/>
                <h2 className="px-3 pt-4 text-xs font-semibold uppercase text-slate-500">Notes de frais</h2>
                {employees.map(emp => <NavItem key={emp.id} icon={<User size={16}/>} label={`${emp.firstName} ${emp.lastName}`} onClick={() => setPage('expenses', emp.id)} isActive={currentPage === 'expenses' && selectedEmployeeId === emp.id}/>)}
            </nav>
            <div className="space-y-2 border-t border-slate-800 pt-4">
                 <NavItem icon={<FileCog size={18}/>} label="Clients & Tarifs" onClick={() => setPage('clients')} isActive={currentPage === 'clients'}/>
                <NavItem icon={<Settings size={18}/>} label="Paramètres" onClick={() => setPage('settings')} isActive={currentPage === 'settings'}/>
                <NavItem icon={<LogOut size={18}/>} label="Déconnexion" onClick={logout}/>
            </div>
        </aside>
    );
};

// --- PAGE PRINCIPALE ---
const MainPage = () => {
    const { currentPage, selectedEmployeeId } = useAppStore();
    switch (currentPage) {
        case 'expenses': return <ExpensesPage key={selectedEmployeeId} />;
        case 'settings': return <SettingsPage />;
        case 'clients': return <ClientsPage />;
        case 'dashboard':
        default: return <DashboardPage />;
    }
};

// --- TABLEAU DE BORD ---
const DashboardPage = () => {
    const { employees, clients, expenses } = useAppStore();
    const totalExpenses = expenses.reduce((acc, exp) => acc + parseFloat(exp.amount), 0);

    return (
        <div className="p-8">
            <h1 className="text-3xl font-bold text-cyan-300 mb-6">Tableau de bord</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-cyan-300">Total Dépenses</CardTitle>
                        <Bot className="h-4 w-4 text-slate-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{totalExpenses.toFixed(2)} €</div>
                        <p className="text-xs text-slate-400">Sur {expenses.length} notes de frais</p>
                    </CardContent>
                </Card>
                 <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-cyan-300">Employés</CardTitle>
                        <Users className="h-4 w-4 text-slate-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{employees.length}</div>
                        <p className="text-xs text-slate-400">Employés enregistrés</p>
                    </CardContent>
                </Card>
                 <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-cyan-300">Clients</CardTitle>
                        <Users className="h-4 w-4 text-slate-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{clients.length}</div>
                        <p className="text-xs text-slate-400">Clients enregistrés</p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

// --- PAGE DES DÉPENSES ---
const ExpensesPage = () => {
    const { expenses, clients, settings, selectedEmployeeId, addExpense, setExpenses, employees, deleteExpense } = useAppStore();
    const currentEmployee = employees.find(e => e.id === selectedEmployeeId);
    
    const [filteredExpenses, setFilteredExpenses] = useState([]);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterCategory, setFilterCategory] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });
    const [newExpense, setNewExpense] = useState({date: new Date().toISOString().split('T')[0],clientId: '',km: '',category: 'Déplacement'});
    const [isPurgeModalOpen, setIsPurgeModalOpen] = useState(false);
    const [pdfLibsLoaded, setPdfLibsLoaded] = useState(false);
    const [expenseToDelete, setExpenseToDelete] = useState(null);
    
    useEffect(() => {
        const jspdfScript = document.createElement('script');
        jspdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        jspdfScript.async = true;
        jspdfScript.onload = () => {
            const autotableScript = document.createElement('script');
            autotableScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
            autotableScript.async = true;
            autotableScript.onload = () => setPdfLibsLoaded(true);
            document.body.appendChild(autotableScript);
        };
        document.body.appendChild(jspdfScript);
        return () => { document.querySelectorAll('script[src*="jspdf"]').forEach(s => s.remove()); }
    }, []);

    useEffect(() => {
        let data = expenses.filter(exp => exp.employeeId === selectedEmployeeId);
        data = data.filter(exp => {
            const expDate = new Date(exp.date);
            const yearMatch = expDate.getFullYear() === Number(filterYear);
            const monthMatch = expDate.getUTCMonth() + 1 === Number(filterMonth);
            const categoryMatch = filterCategory === 'all' || exp.category === filterCategory;
            return yearMatch && monthMatch && categoryMatch;
        });
        if (sortConfig.key) {
            data.sort((a, b) => {
                let aValue = a[sortConfig.key]; let bValue = b[sortConfig.key];
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        setFilteredExpenses(data);
    }, [expenses, selectedEmployeeId, filterYear, filterMonth, filterCategory, sortConfig]);

    const handleAddExpense = async (e) => {
        e.preventDefault();
        const client = clients.find(c => c.id === parseInt(newExpense.clientId, 10));
        const km = parseFloat(newExpense.km);
        const expenseToSave = {
            employeeId: selectedEmployeeId,
            clientId: parseInt(newExpense.clientId, 10),
            date: newExpense.date,
            km,
            amount: km * (settings?.mileageRate || 0),
            category: newExpense.category,
        };

        try {
            const response = await fetch(`${API_URL}/expenses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(expenseToSave)
            });
            const savedExpense = await response.json();
            if (response.ok) {
                addExpense(savedExpense);
                setNewExpense({date: new Date().toISOString().split('T')[0],clientId: '',km: '',category: 'Déplacement'});
            } else {
                alert("Erreur: " + savedExpense.message);
            }
        } catch (error) {
            alert("Erreur de connexion avec le serveur.");
        }
    };
    
    const handlePurgeMonth = async () => {
        try {
            const response = await fetch(`${API_URL}/expenses/purge`, {
                method: 'POST', // Changé de DELETE à POST
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ employeeId: selectedEmployeeId, year: filterYear, month: filterMonth })
            });
            if(response.ok) {
                const updatedExpenses = expenses.filter(exp => {
                    const d = new Date(exp.date);
                    return !(exp.employeeId === selectedEmployeeId && d.getFullYear() === filterYear && d.getUTCMonth() + 1 === filterMonth)
                });
                setExpenses(updatedExpenses);
            } else {
                 alert("Erreur lors de la purge.");
            }
        } catch (error) {
             alert("Erreur de connexion avec le serveur.");
        }
        setIsPurgeModalOpen(false);
    };

    const handleConfirmDeleteExpense = async () => {
        if (!expenseToDelete) return;
        try {
            const response = await fetch(`${API_URL}/expenses/${expenseToDelete.id}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                deleteExpense(expenseToDelete.id);
            } else {
                alert("Erreur lors de la suppression de la dépense.");
            }
        } catch (error) {
            alert("Erreur de connexion avec le serveur.");
        }
        setExpenseToDelete(null);
    };

    const generatePDF = () => {
        if (!currentEmployee || !settings || !pdfLibsLoaded) return;
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(16); doc.setFont(undefined, 'bold'); doc.text(settings.companyName, 14, 20);
        doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.text(`${settings.companyAddress}, ${settings.companyPostalCode} ${settings.companyCity}`, 14, 26);
        doc.setFontSize(14); doc.setFont(undefined, 'bold'); doc.text(`Note de frais de ${currentEmployee.lastName.toUpperCase()} ${currentEmployee.firstName}`, doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
        const lastDayOfMonth = new Date(filterYear, filterMonth, 0);
        const reportDate = `Période du ${filterMonth}/${filterYear} - Rapport du ${lastDayOfMonth.toLocaleDateString('fr-FR')}`;
        doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.text(reportDate, doc.internal.pageSize.getWidth() - 14, 20, { align: 'right' });
        doc.text(`Barème kilométrique: ${settings.mileageRate} €/km`, doc.internal.pageSize.getWidth() - 14, 26, { align: 'right' });
        
        const tableColumn = ["Date", "Client", "Catégorie", "KM", "Montant (€)"];
        const tableRows = []; let totalKm = 0; let totalAmount = 0;
        filteredExpenses.forEach(expense => {
            tableRows.push([new Date(expense.date).toLocaleDateString('fr-FR'), expense.clientName, expense.category, parseFloat(expense.km).toFixed(2), parseFloat(expense.amount).toFixed(2)]);
            totalKm += parseFloat(expense.km); totalAmount += parseFloat(expense.amount);
        });
        tableRows.push([{ content: 'TOTAL', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right' } }, { content: totalKm.toFixed(2), styles: { fontStyle: 'bold' } },{ content: totalAmount.toFixed(2), styles: { fontStyle: 'bold' } }]);
        doc.autoTable({ head: [tableColumn], body: tableRows, startY: 40, theme: 'grid', headStyles: { fillColor: [0, 150, 199] }});
        doc.save(`notes_de_frais_${currentEmployee.lastName}_${filterYear}_${filterMonth}.pdf`);
    };

    const generateOutlookLink = (expense) => {
        const startDate = new Date(expense.date);
        startDate.setUTCHours(9); // 9h du matin UTC
        const endDate = new Date(startDate);
        endDate.setUTCHours(10); // 10h du matin UTC

        const params = new URLSearchParams({
            path: '/calendar/action/compose',
            rru: 'addevent',
            startdt: startDate.toISOString().replace(/-|:|\.\d+/g, ''),
            enddt: endDate.toISOString().replace(/-|:|\.\d+/g, ''),
            subject: `Déplacement - ${expense.clientName}`,
            body: `Note de frais pour le client ${expense.clientName}.<br>Distance : ${expense.km} km.`,
            location: expense.clientName
        });

        return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div><h1 className="text-3xl font-bold text-cyan-300">Dépenses de {currentEmployee?.firstName} {currentEmployee?.lastName}</h1></div>
                <div className="flex items-center gap-2">
                    <Button onClick={generatePDF} variant="outline" size="sm" disabled={filteredExpenses.length === 0 || !pdfLibsLoaded}><FileDown className="mr-2 h-4 w-4" />{pdfLibsLoaded ? 'PDF (Liste)' : 'Chargement...'}</Button>
                    <Button onClick={() => setIsPurgeModalOpen(true)} variant="destructive" size="sm" disabled={filteredExpenses.length === 0}><Trash2 className="mr-2 h-4 w-4" />Purger</Button>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card><CardContent className="pt-6"><div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div><Label htmlFor="year-filter">Année</Label><Select id="year-filter" value={filterYear} onChange={e => setFilterYear(Number(e.target.value))}>{[...Array(5)].map((_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</Select></div>
                        <div><Label htmlFor="month-filter">Mois</Label><Select id="month-filter" value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))}>{[...Array(12)].map((_, i) => <option key={i} value={i + 1}>{new Date(0, i).toLocaleString('fr-FR', { month: 'long' })}</option>)}</Select></div>
                        <div><Label htmlFor="category-filter">Catégorie</Label><Select id="category-filter" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}><option value="all">Toutes</option><option>Déplacement</option><option>Repas</option><option>Hébergement</option><option>Autre</option></Select></div>
                    </div></CardContent></Card>
                    <Card>
                         <CardHeader><CardTitle>Liste des dépenses</CardTitle><CardDescription>Total: <span className="font-bold text-cyan-400">{filteredExpenses.reduce((acc, curr) => acc + parseFloat(curr.amount), 0).toFixed(2)} €</span></CardDescription></CardHeader>
                        <CardContent><div className="overflow-x-auto"><table className="w-full text-sm text-left text-slate-300">
                            <thead className="text-xs text-cyan-300 uppercase bg-slate-800"><tr>{['date','clientName','category','km','amount', 'actions'].map(key=>(<th key={key} scope="col" className="px-6 py-3">{({date:'Date',clientName:'Client',category:'Catégorie',km:'KM',amount:'Montant', actions: 'Actions'})[key]}</th>))}</tr></thead>
                            <tbody>{filteredExpenses.length>0?filteredExpenses.map(exp=>(<tr key={exp.id} className="border-b border-slate-700 hover:bg-slate-800/50">
                                <td className="px-6 py-4">{new Date(exp.date).toLocaleDateString('fr-FR')}</td>
                                <td className="px-6 py-4">{exp.clientName}</td>
                                <td className="px-6 py-4">{exp.category}</td>
                                <td className="px-6 py-4">{parseFloat(exp.km).toFixed(2)}</td>
                                <td className="px-6 py-4 font-medium text-cyan-400">{parseFloat(exp.amount).toFixed(2)} €</td>
                                <td className="px-6 py-4 flex items-center">
                                    <a href={generateOutlookLink(exp)} target="_blank" rel="noopener noreferrer">
                                        <Button variant="ghost" size="icon">
                                            <CalendarPlus className="h-4 w-4 text-cyan-400" />
                                        </Button>
                                    </a>
                                    <Button variant="ghost" size="icon" onClick={() => setExpenseToDelete(exp)}>
                                        <Trash2 className="h-4 w-4 text-red-400" />
                                    </Button>
                                </td>
                            </tr>)):(<tr><td colSpan="6" className="px-6 py-4 text-center text-slate-400">Aucune dépense.</td></tr>)}</tbody>
                        </table></div></CardContent>
                    </Card>
                </div>
                <div className="space-y-6">
                    <Card><CardHeader><CardTitle>Ajouter une dépense</CardTitle></CardHeader><CardContent><form onSubmit={handleAddExpense} className="space-y-4">
                        <div><Label htmlFor="date">Date</Label><Input id="date" type="date" value={newExpense.date} onChange={e=>setNewExpense({...newExpense,date:e.target.value})} required/></div>
                        <div><Label htmlFor="client">Client</Label><Select id="client" value={newExpense.clientId} onChange={e=>setNewExpense({...newExpense,clientId:e.target.value})} required><option value="">Choisir...</option>{clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</Select></div>
                        <div><Label htmlFor="km">Kilomètres</Label><Input id="km" type="number" step="0.01" placeholder="ex: 85.5" value={newExpense.km} onChange={e=>setNewExpense({...newExpense,km:e.target.value})} required/></div>
                        <div><Label htmlFor="category">Catégorie</Label><Select id="category" value={newExpense.category} onChange={e=>setNewExpense({...newExpense,category:e.target.value})} required><option>Déplacement</option><option>Repas</option><option>Hébergement</option><option>Autre</option></Select></div>
                        <Button type="submit" className="w-full"><PlusCircle className="mr-2 h-4 w-4"/>Ajouter</Button>
                    </form></CardContent></Card>
                    <AutoGenerateExpenses />
                </div>
            </div>
            <Modal isOpen={isPurgeModalOpen} onClose={()=>setIsPurgeModalOpen(false)} title="Confirmation"><p className="text-slate-300 mb-6">Supprimer les dépenses de <span className="font-bold text-cyan-400">{new Date(0,filterMonth-1).toLocaleString('fr-FR',{month:'long'})} {filterYear}</span> ?</p><div className="flex justify-end gap-4"><Button variant="ghost" onClick={()=>setIsPurgeModalOpen(false)}>Annuler</Button><Button variant="destructive" onClick={handlePurgeMonth}>Confirmer</Button></div></Modal>
            <Modal isOpen={!!expenseToDelete} onClose={()=>setExpenseToDelete(null)} title="Confirmer la suppression">
                <p className="text-slate-300 mb-6">Êtes-vous sûr de vouloir supprimer ce déplacement du <span className="font-bold text-cyan-400">{expenseToDelete && new Date(expenseToDelete.date).toLocaleDateString('fr-FR')}</span> pour le client <span className="font-bold text-cyan-400">{expenseToDelete?.clientName}</span> ?</p>
                <div className="flex justify-end gap-4"><Button variant="ghost" onClick={()=>setExpenseToDelete(null)}>Annuler</Button><Button variant="destructive" onClick={handleConfirmDeleteExpense}>Confirmer</Button></div>
            </Modal>
        </div>
    );
};

// --- COMPOSANT DE GÉNÉRATION AUTO ---
const AutoGenerateExpenses = () => {
    const { settings, selectedEmployeeId, setExpenses } = useAppStore();
    const [targetAmount, setTargetAmount] = useState(2000);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationResult, setGenerationResult] = useState(null);

    const handleGenerate = async () => {
        setIsGenerating(true); setGenerationResult(null);
        try {
            const response = await fetch(`${API_URL}/expenses/generate-spread`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    employeeId: selectedEmployeeId,
                    year, month, targetAmount,
                    mileageRate: settings.mileageRate,
                })
            });
            const result = await response.json();
            if(response.ok) {
                setExpenses(result.expenses);
                setGenerationResult({ success: true, message: result.message });
            } else {
                setGenerationResult({ success: false, message: result.message });
            }
        } catch(error) {
            setGenerationResult({ success: false, message: "Erreur de connexion serveur." });
        }
        setIsGenerating(false);
    };
    
    return (<Card><CardHeader><CardTitle>Génération Automatique</CardTitle><CardDescription>Répartir les frais sur le mois.</CardDescription></CardHeader><CardContent className="space-y-4"><div className="grid grid-cols-2 gap-4"><div><Label>Année</Label><Select value={year} onChange={e=>setYear(Number(e.target.value))} disabled={isGenerating}>{[...Array(5)].map((_,i)=><option key={i} value={new Date().getFullYear()-i}>{new Date().getFullYear()-i}</option>)}</Select></div><div><Label>Mois</Label><Select value={month} onChange={e=>setMonth(Number(e.target.value))} disabled={isGenerating}>{[...Array(12)].map((_,i)=><option key={i} value={i+1}>{new Date(0,i).toLocaleString('fr-FR',{month:'long'})}</option>)}</Select></div></div><div><Label>Montant cible (€)</Label><Input type="number" value={targetAmount} onChange={e=>setTargetAmount(Number(e.target.value))} disabled={isGenerating}/></div><Button onClick={handleGenerate} disabled={isGenerating||!selectedEmployeeId} className="w-full"><Bot className="mr-2 h-4 w-4"/>{isGenerating?'Génération...':'Générer'}</Button>
    {generationResult&&<div className={cn("mt-4 rounded-md p-3 text-sm",generationResult.success?"bg-green-500/20 text-green-300":"bg-red-500/20 text-red-300")}>{generationResult.success?`Succès! ${generationResult.message}`: `Erreur: ${generationResult.message}`}</div>}
    </CardContent></Card>);
};

// --- PAGE CLIENTS ---
const ClientsPage = () => {
    const { clients, addClient, deleteClient } = useAppStore();
    const [isEditing, setIsEditing] = useState(null);
    const [currentClient, setCurrentClient] = useState({ name: '', defaultKm: '' });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [clientToDelete, setClientToDelete] = useState(null);
    
    const handleSave = async (e) => {
        e.preventDefault();
        const dataToSave = { name: currentClient.name, defaultKm: Number(currentClient.defaultKm) };
        try {
            const response = await fetch(`${API_URL}/clients`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSave)
            });
            const newClient = await response.json();
            if(response.ok) addClient(newClient);
            else alert("Erreur: " + newClient.message);
        } catch (error) { alert("Erreur de connexion avec le serveur."); }
        setIsEditing(null);
    };

    const confirmDelete = async () => {
        if (clientToDelete) {
            try {
                const response = await fetch(`${API_URL}/clients/${clientToDelete.id}`, { method: 'DELETE' });
                if(response.ok) deleteClient(clientToDelete.id);
                else alert("Erreur lors de la suppression.");
            } catch (error) { alert("Erreur de connexion avec le serveur."); }
            setIsDeleteModalOpen(false);
            setClientToDelete(null);
        }
    };
    
    const handleAddNew = () => { setIsEditing('new'); setCurrentClient({ name: '', defaultKm: '' }); };
    const handleCancel = () => { setIsEditing(null); setCurrentClient({ name: '', defaultKm: '' }); };
    const openDeleteModal = (client) => { setClientToDelete(client); setIsDeleteModalOpen(true); };

    return (
        <div className="p-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-cyan-300">Clients et Tarifs</h1>
                <Button onClick={handleAddNew}><PlusCircle className="mr-2 h-4 w-4"/>Ajouter un client</Button>
            </div>
            <Card>
                <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-slate-300">
                            <thead className="text-xs text-cyan-300 uppercase bg-slate-800">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Nom du Client</th>
                                    <th scope="col" className="px-6 py-3">KM par défaut</th>
                                    <th scope="col" className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map(client => (
                                    <tr key={client.id} className="border-b border-slate-700 hover:bg-slate-800/50">
                                        <td className="px-6 py-4">{client.name}</td>
                                        <td className="px-6 py-4">{client.defaultKm}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="destructive" size="sm" onClick={() => openDeleteModal(client)}>Supprimer</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Modal isOpen={!!isEditing} onClose={handleCancel} title="Ajouter un Client">
                <form onSubmit={handleSave} className="space-y-4">
                    <div><Label>Nom du client</Label><Input value={currentClient.name} onChange={e=>setCurrentClient({...currentClient, name: e.target.value})} required/></div>
                    <div><Label>KM par défaut</Label><Input type="number" value={currentClient.defaultKm} onChange={e=>setCurrentClient({...currentClient, defaultKm: e.target.value})} required/></div>
                    <div className="flex justify-end gap-4 pt-4"><Button type="button" variant="ghost" onClick={handleCancel}>Annuler</Button><Button type="submit">Enregistrer</Button></div>
                </form>
            </Modal>
            <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Confirmer la suppression">
                <p className="text-slate-300 mb-6">Êtes-vous sûr de vouloir supprimer le client <span className="font-bold text-cyan-400">{clientToDelete?.name}</span> ?</p>
                <div className="flex justify-end gap-4"><Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)}>Annuler</Button><Button variant="destructive" onClick={confirmDelete}>Confirmer</Button></div>
            </Modal>
        </div>
    );
};

// --- PAGE PARAMÈTRES ---
const SettingsPage = () => {
    const { settings, updateSettings } = useAppStore();
    const [formState, setFormState] = useState(settings);
    
    useEffect(() => { setFormState(settings); }, [settings]);
    const handleChange = (e) => setFormState(prev => ({ ...prev, [e.target.name]: e.target.value }));
    
    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_URL}/settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formState),
            });
            const savedSettings = await response.json();
            if(response.ok) {
                updateSettings(savedSettings);
                alert("Paramètres enregistrés !");
            } else {
                alert("Erreur: " + savedSettings.message);
            }
        } catch (error) {
            alert("Erreur de connexion serveur.");
        }
    };
    
    if(!formState) return null;
    return (
        <div className="p-8">
            <h1 className="text-3xl font-bold text-cyan-300 mb-6">Paramètres</h1>
            <Card><form onSubmit={handleSave}><CardContent className="pt-6 space-y-8">
                <div className="space-y-4"><h2 className="text-xl font-semibold text-cyan-400 border-b border-slate-700 pb-2">Informations société</h2><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1"><Label>Nom</Label><Input name="companyName" value={formState.companyName} onChange={handleChange}/></div>
                    <div className="space-y-1"><Label>Adresse</Label><Input name="companyAddress" value={formState.companyAddress} onChange={handleChange}/></div>
                    <div className="space-y-1"><Label>Code Postal</Label><Input name="companyPostalCode" value={formState.companyPostalCode} onChange={handleChange}/></div>
                    <div className="space-y-1"><Label>Ville</Label><Input name="companyCity" value={formState.companyCity} onChange={handleChange}/></div>
                </div></div>
                <div className="space-y-4"><h2 className="text-xl font-semibold text-cyan-400 border-b border-slate-700 pb-2">Paramètres application</h2><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1"><Label>Barème (€/km)</Label><Input name="mileageRate" type="number" step="0.01" value={formState.mileageRate} onChange={handleChange}/></div>
                    <div className="space-y-1"><Label>Code PIN (4 chiffres)</Label><Input name="loginPin" maxLength="4" value={formState.loginPin} onChange={handleChange}/></div>
                </div></div>
            </CardContent><CardFooter><Button type="submit">Enregistrer</Button></CardFooter></form></Card>
        </div>
    );
};

// --- Composant pour l'erreur API ---
const ApiErrorScreen = ({ message }) => (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-900 p-8 font-sans text-slate-50">
        <Card className="z-10 w-full max-w-lg border-red-500/50 bg-red-900/20">
            <CardHeader className="flex-row items-center gap-4">
                <AlertTriangle className="h-8 w-8 text-red-400" />
                <CardTitle className="text-red-400">Erreur de Connexion</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="text-slate-300">{message}</p>
                <p className="mt-4 text-sm text-slate-400">
                    Merci de vérifier que votre serveur backend est bien démarré (avec <code className="bg-slate-700 text-cyan-300 px-1 py-0.5 rounded">node server.js</code> ou <code className="bg-slate-700 text-cyan-300 px-1 py-0.5 rounded">pm2</code>) et que la variable <code className="bg-slate-700 text-cyan-300 px-1 py-0.5 rounded">API_URL</code> dans le code du frontend est correcte.
                </p>
            </CardContent>
        </Card>
    </div>
);


// --- COMPOSANT RACINE DE L'APPLICATION ---
const App = () => {
    const { isAuthenticated, fetchInitialData, apiError } = useAppStore();

    useEffect(() => {
        document.title = 'NDF'; // Changer le titre de l'onglet
        fetchInitialData();
    }, [fetchInitialData]);

    if (apiError) {
        return <ApiErrorScreen message={apiError} />;
    }
    
    if (!isAuthenticated) {
        return <LoginScreen />;
    }

    return (
        <div className="flex h-screen bg-slate-950 font-sans text-slate-50">
            <Sidebar />
            <main className="flex-1 overflow-y-auto bg-grid-slate-800/[0.2]">
                <div className="h-full w-full bg-gradient-to-br from-slate-900/10 via-transparent to-slate-900/10">
                    <MainPage />
                </div>
            </main>
        </div>
    );
};

export default App;
